import { useState, useEffect, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';

interface Alien2048SettingsPageProps {
  twitterUser: unknown;
  onTwitterSignIn: () => void;
  onTwitterSignOut: () => void;
}

// Fixed dimensions for mobile-first design
const SCREEN_WIDTH = 375;
const SCREEN_HEIGHT = 667;

// Small X logo for credits
const XLogoSmall = () => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="currentColor"
    style={{ marginRight: '6px', verticalAlign: 'middle' }}
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

function useResponsiveScale(): number {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const calculateScale = () => {
      const scaleX = window.innerWidth / SCREEN_WIDTH;
      const scaleY = window.innerHeight / SCREEN_HEIGHT;
      setScale(Math.min(scaleX, scaleY, 1.5));
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, []);

  return scale;
}

/**
 * Game-specific settings page for Alien 2048.
 * Contains only Audio and Credits. Account settings are in /settings.
 */
function Alien2048SettingsPage(_props: Alien2048SettingsPageProps) {
  const navigate = useNavigate();
  const scale = useResponsiveScale();

  // Audio settings
  const [musicEnabled, setMusicEnabled] = useState(() => {
    const stored = localStorage.getItem('alien2048_music');
    return stored !== 'false';
  });
  const [sfxEnabled, setSfxEnabled] = useState(() => {
    const stored = localStorage.getItem('alien2048_sfx');
    return stored !== 'false';
  });

  // Hide app header
  useEffect(() => {
    const header = document.querySelector('.app-header');
    if (header) {
      (header as HTMLElement).style.display = 'none';
    }
    return () => {
      if (header) {
        (header as HTMLElement).style.display = '';
      }
    };
  }, []);

  const handleMusicToggle = () => {
    const newValue = !musicEnabled;
    setMusicEnabled(newValue);
    localStorage.setItem('alien2048_music', String(newValue));
  };

  const handleSfxToggle = () => {
    const newValue = !sfxEnabled;
    setSfxEnabled(newValue);
    localStorage.setItem('alien2048_sfx', String(newValue));
  };

  const containerStyle: CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#0a0a1a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  };

  const pageStyle: CSSProperties = {
    width: SCREEN_WIDTH,
    height: SCREEN_HEIGHT,
    transform: `scale(${scale})`,
    transformOrigin: 'center center',
    backgroundColor: '#0a0a1a',
    display: 'flex',
    flexDirection: 'column',
    padding: '16px',
    boxSizing: 'border-box',
    overflowY: 'auto',
  };

  return (
    <div style={containerStyle}>
      <div style={pageStyle}>
        {/* Header */}
        <div style={styles.header}>
          <button style={styles.backButton} onClick={() => navigate('/alien2048/')}>
            ← Back
          </button>
          <h1 style={styles.title}>Settings</h1>
          <div style={styles.headerSpacer} />
        </div>

        {/* Audio Settings */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Audio</div>

          <div style={styles.toggleRow} onClick={handleMusicToggle}>
            <span style={styles.toggleLabel}>Music</span>
            <div style={{ ...styles.toggle, ...(musicEnabled ? styles.toggleOn : {}) }}>
              <div style={{ ...styles.toggleKnob, ...(musicEnabled ? styles.toggleKnobOn : {}) }} />
            </div>
          </div>

          <div style={styles.toggleRow} onClick={handleSfxToggle}>
            <span style={styles.toggleLabel}>Sound Effects</span>
            <div style={{ ...styles.toggle, ...(sfxEnabled ? styles.toggleOn : {}) }}>
              <div style={{ ...styles.toggleKnob, ...(sfxEnabled ? styles.toggleKnobOn : {}) }} />
            </div>
          </div>
        </div>

        {/* Credits Section */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Credits</div>

          <div style={styles.creditRow}>
            <span style={styles.creditLabel}>Development</span>
            <a
              href="https://x.com/_nngmi"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.creditLink}
            >
              <XLogoSmall /> _nngmi
            </a>
          </div>

          <div style={styles.creditRow}>
            <span style={styles.creditLabel}>Music</span>
            <span style={styles.creditText}>Tvari</span>
          </div>
        </div>

        {/* Link to Account Settings */}
        <button style={styles.accountLink} onClick={() => navigate('/settings')}>
          Account Settings →
        </button>
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  backButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: '#888',
    border: '1px solid #333',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '14px',
    cursor: 'pointer',
  },
  title: {
    color: '#4ade80',
    fontSize: '28px',
    fontWeight: 'bold',
    margin: 0,
  },
  headerSpacer: {
    width: '80px',
  },
  section: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '16px',
    border: '1px solid #333',
  },
  sectionTitle: {
    color: '#4ade80',
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '12px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  toggleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    cursor: 'pointer',
  },
  toggleLabel: {
    color: '#fff',
    fontSize: '14px',
  },
  toggle: {
    width: '48px',
    height: '28px',
    backgroundColor: '#333',
    borderRadius: '14px',
    position: 'relative',
    transition: 'background-color 0.2s',
  },
  toggleOn: {
    backgroundColor: '#4ade80',
  },
  toggleKnob: {
    width: '24px',
    height: '24px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    position: 'absolute',
    top: '2px',
    left: '2px',
    transition: 'left 0.2s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
  },
  toggleKnobOn: {
    left: '22px',
  },
  creditRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid #333',
  },
  creditLabel: {
    color: '#666',
    fontSize: '14px',
  },
  creditLink: {
    color: '#fff',
    textDecoration: 'none',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
  },
  creditText: {
    color: '#888',
    fontSize: '14px',
  },
  accountLink: {
    backgroundColor: 'rgba(74, 222, 128, 0.1)',
    color: '#4ade80',
    border: '1px solid #4ade80',
    borderRadius: '12px',
    padding: '16px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    textAlign: 'center',
  },
};

export default Alien2048SettingsPage;
